import { Box, Link, Typography } from '@mui/material';
import type { FactEdit, NewsEditSource } from '../store/slices/admin-slice';

interface FactItemPreviewProps {
  readonly values: FactEdit;
  readonly sources: ReadonlyArray<NewsEditSource>;
}

/**
 * `<FactItemPreview>` — live preview of how a fact-of-the-day
 * record will render with the in-progress edit values. Mirrors the
 * Daily-page's `<FactSection>` body layout (floated image + body
 * + inline "Read more at" sources line).
 */
export function FactItemPreview({ values, sources }: FactItemPreviewProps): JSX.Element {
  const { body, image } = values;
  return (
    <Box sx={{ overflow: 'hidden' }}>
      {image && (
        <Box
          component="img"
          src={image}
          alt="Fact of the day"
          sx={{
            float: 'left',
            width: 'auto',
            height: 'auto',
            maxWidth: '50%',
            maxHeight: 200,
            objectFit: 'contain',
            borderRadius: 1,
            mr: 2,
            mb: 1,
          }}
        />
      )}
      {body && <Typography variant="body1">{body}</Typography>}
      {sources.length > 0 && (
        <Typography variant="body1" sx={{ mt: 1 }}>
          Read more at{' '}
          {sources.map((source, idx) => {
            const label = source.name || 'source';
            const href = source.url;
            return (
              <Box component="span" key={`${label}-${idx}`}>
                {idx > 0 && (idx === sources.length - 1 ? ' and ' : ', ')}
                {href ? (
                  <Link href={href} rel="noopener noreferrer" target="_blank">
                    {label}
                  </Link>
                ) : (
                  <Box component="span">{label}</Box>
                )}
              </Box>
            );
          })}
          .
        </Typography>
      )}
    </Box>
  );
}
